import React, { useLayoutEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useOpen } from 'src/hooks/use-open';
import { getTestId } from 'src/helpers/test-helpers';

import Icons from 'assets/icons';

interface CollapsibleFiltersSectionProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const CollapsibleFiltersSection = ({
  title,
  defaultOpen = false,
  children,
}: CollapsibleFiltersSectionProps) => {
  const { isOpen, handleToggle } = useOpen(defaultOpen);
  const [height, setHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen, children]);

  const dataTestId = getTestId({
    prefix: 'collapsible-filter-section',
    label: title,
    suffix: 'btn',
  });

  return (
    <div className="flex flex-col gap-3 w-full">
      <button
        type="button"
        className="flex items-center w-full cursor-pointer"
        onClick={handleToggle}
        data-testid={dataTestId}
      >
        <span className="flex-1 text-left text-sm font-semibold text-gray-700">
          {title}
        </span>
        <Icons.ChevronRight
          className={twMerge(
            'w-4 h-4 text-gray-700 transition-transform',
            isOpen && 'rotate-90'
          )}
        />
      </button>
      <div
        ref={contentRef}
        inert={!isOpen}
        className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
        style={{ maxHeight: isOpen ? `${height}px` : '0px' }}
      >
        <div className="flex flex-col gap-3 pb-3">{children}</div>
      </div>
    </div>
  );
};

export default CollapsibleFiltersSection;
